'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  getLocalizedMarketingPathname,
  resolveLocaleOrDefault,
  SUPPORTED_LOCALES,
} from '@kit/i18n';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../shadcn/select';

export function LanguageSelector({
  onChange,
  classNameTrigger,
}: {
  onChange?: (locale: string) => unknown;
  classNameTrigger?: string;
}) {
  const { i18n } = useTranslation();
  const { language: currentLanguage, options } = i18n;
  const supabase = useSupabase();

  const normalizedCurrentLanguage = useMemo(
    () => normalizeLocale(currentLanguage),
    [currentLanguage],
  );

  const locales = useMemo(() => {
    const configuredSupportedLngs = options.supportedLngs;
    const supported = Array.isArray(configuredSupportedLngs)
      ? configuredSupportedLngs.filter(
          (locale) => locale.toLowerCase() !== 'cimode',
        )
      : [...SUPPORTED_LOCALES];

    return Array.from(new Set(supported.map((locale) => normalizeLocale(locale))));
  }, [options.supportedLngs]);

  const languageNames = useMemo(() => {
    return new Intl.DisplayNames([normalizedCurrentLanguage], {
      type: 'language',
    });
  }, [normalizedCurrentLanguage]);

  const [value, setValue] = useState(normalizedCurrentLanguage);

  useEffect(() => {
    setValue(normalizedCurrentLanguage);
  }, [normalizedCurrentLanguage]);

  const hasSelectedValue = locales.includes(value);
  const selectedValue = hasSelectedValue ? value : undefined;
  const placeholderLocale = hasSelectedValue
    ? value
    : (locales[0] ?? normalizedCurrentLanguage);
  const selectedLabel = capitalize(
    languageNames.of(placeholderLocale) ?? placeholderLocale,
  );

  const languageChanged = useCallback(
    async (locale: string) => {
      const targetLocale = normalizeLocale(locale);
      setValue(targetLocale);

      if (onChange) {
        void onChange(targetLocale);
      }

      await i18n.changeLanguage(targetLocale);

      // If the user is authenticated, update user_metadata.locale in Supabase
      void supabase.auth.getUser().then((user) => {
        if (user) {
          void supabase.auth.updateUser({
            data: { locale: targetLocale },
          });
        }
      });

      const { pathname, search, hash } = window.location;
      const nextPathname = getLocalizedMarketingPathname(pathname, targetLocale);
      const nextUrl = `${nextPathname}${search}${hash}`;

      if (nextUrl === `${pathname}${search}${hash}`) {
        window.location.reload();
        return;
      }

      window.location.assign(nextUrl);
    },
    [i18n, onChange, supabase],
  );

  return (
    <Select value={selectedValue} onValueChange={languageChanged}>
      <SelectTrigger className={classNameTrigger}>
        <SelectValue placeholder={selectedLabel} />
      </SelectTrigger>

      <SelectContent>
        {locales.map((locale) => {
          const label = capitalize(languageNames.of(locale) ?? locale);

          const option = {
            value: locale,
            label,
          };

          return (
            <SelectItem value={option.value} key={option.value}>
              {option.label}
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

function capitalize(lang: string) {
  return lang.slice(0, 1).toUpperCase() + lang.slice(1);
}

function normalizeLocale(locale: string | undefined) {
  const baseLocale = locale?.split('-')[0]?.toLowerCase();
  return resolveLocaleOrDefault(baseLocale);
}
